import React from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import ReactDOM from "react-dom/client";
import { Provider } from 'react-redux';
import { store } from './redux/store';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "./pages/SharedComponents/css/data-scroller.css"
import './assets/vendor/css/pages/page-auth.css';
import "primereact/resources/primereact.css";
import 'react-phone-input-2/lib/style.css'
import "primeicons/primeicons.css";
import "./index.css";
import "./App.css";
// Design-system theme tokens — SOURCE OF TRUTH for colors. Defines
// :root (light), [data-theme="dark"], and prefers-color-scheme cascade.
// Must come before pp-overrides.css so the override file can consume
// var(--pp-*).
import "./styles/pp-theme.css";
// Design-system overrides on top of PrimeReact — imported AFTER theme.css
// so the cascade wins. One file overrides Dialog / DataTable / Button /
// Dropdown / InputText / Tag globally per design-system.md.
import "./styles/pp-overrides.css";
// Shared component layer (stat tiles, panels, badges, empty states) — no
// consumer yet, imported so it's ready to use; adds no classNames to any
// existing page, so this import alone is zero-diff.
import "./styles/pp-components.css";
// Apply persisted theme choice BEFORE React renders so first paint is
// already in the right mode (no light-flash on dark-mode users).
import { bootstrapTheme } from "./pages/SharedComponents/ThemeToggle";
// Initialize i18n + apply document.dir / lang BEFORE first render.
import "./i18n";
import { ScrollLoadingProvider } from "./pages/Contexts/ScrollLoadingContext";
import { DataTableProvider } from "./pages/Contexts/DataTableContext";
import { GlobalToastProvider } from "./pages/Contexts/ToastContext";
import { GalleryProvider } from "./pages/Contexts/GalleryContext";
import PrimeReact from 'primereact/api';
import RoutesContainer from "./routes/RoutesContainer";

// Apply persisted theme choice BEFORE React renders so first paint is
// already in the right mode (no light-flash on dark-mode users).
bootstrapTheme();

// Guard a portal host node's removeChild/insertBefore so PrimeReact v8 overlays
// injecting/removing DOM outside React 18's reconciler tracking never throw
// (these are cosmetically harmless — the DOM self-corrects). Applied to BOTH the
// React #root and the body-level overlay container below.
function patchPortalHost(node) {
  const origRemove = node.removeChild.bind(node);
  node.removeChild = function patchedRemoveChild(child) {
    if (child.parentNode !== node) return child; // already removed by PrimeReact
    return origRemove(child);
  };
  const origInsert = node.insertBefore.bind(node);
  node.insertBefore = function patchedInsertBefore(newNode, refNode) {
    if (refNode && refNode.parentNode !== node) return node.appendChild(newNode);
    return origInsert(newNode, refNode);
  };
}

// GLOBAL overlay positioning: PrimeReact overlay panels (Dropdown / MultiSelect /
// Calendar, plus Dialogs / Toasts) used to render inline ('self'), which got
// CLIPPED by any dialog/card with overflow:hidden — every dropdown-in-a-dialog
// looked broken. We instead render them into a dedicated container at the end of
// <body>, so they're never clipped and stack correctly (PrimeReact autoZIndex).
// The container is NOT managed by React's #root reconciler; we patch its
// removeChild/insertBefore (same guard as #root) so v8+React18 portal churn is
// safe. This is the SINGLE global fix — no per-Dropdown appendTo needed.
const ppOverlayRoot = document.createElement('div');
ppOverlayRoot.id = 'pp-overlays';
document.body.appendChild(ppOverlayRoot);
patchPortalHost(ppOverlayRoot);
PrimeReact.appendTo = ppOverlayRoot;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't retry on client errors (4xx) — especially 429 (rate limit).
      // Only retry on server errors (5xx), and only once.
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status && status >= 400 && status < 500) return false; // no retry on 4xx
        return failureCount < 1; // retry server errors once
      },
      // Prevent refetch storms when the user tabs back to the app.
      refetchOnWindowFocus: false,
      // Consider data fresh for 30 seconds to avoid duplicate fetches
      // when components mount/unmount quickly.
      staleTime: 30_000,
    },
  },
});

export default function App() {

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <GlobalToastProvider>
          <ScrollLoadingProvider >
            <DataTableProvider >
              <GalleryProvider>
                <RoutesContainer />
              </GalleryProvider>
            </DataTableProvider>
          </ScrollLoadingProvider>
        </GlobalToastProvider >
      </QueryClientProvider>
    </Provider>
  );
}

// Patch the #root container the same way (it still hosts any overlay that
// explicitly opts back into appendTo="self", plus React-rendered portals).
const container = document.getElementById("root");
patchPortalHost(container);

const root = ReactDOM.createRoot(container);
root.render(<App />);
